import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { generatePath, useNavigate } from 'react-router';
import styled from 'styled-components';
import { FieldArray, Formik, FormikErrors } from 'formik';
import moment from 'moment';
import * as Yup from 'yup';
import { AlertShort, AlertSingleLine, DataCard, Loader, SdirButton, SelectOption } from '@sdir/sds';
import { useApi } from '@sdir/httpclient/lib/hooks/useApi';
import {
  EndorsementServiceFeaturesGetAllCertificatesGetAllCertificatesDto,
  EndorsementServiceFeaturesUpsertApplicationGroupCertificate,
  UpsertApplicationGroupRequest,
} from '@sdir/httpclient/lib/clients/aps/endorsement';
import { sortListByAttribute } from '@sdir/blueprint.aps/lib/helpers/formattingHelpers';
import { ErrorMessage } from '@sdir/utilities/lib/components';
import { ApplicationLayout } from '../Organisms/ApplicationLayout';
import ApplicationSteps from '../Molecules/ApplicationSteps';
import { routes } from '../Routes';
import { endorsementApi } from '../../httpclient';
import { useCurrentApplication } from '../../services/hooks/useCurrentApplication';
import { toBase64 } from '../../helpers/fileHelpers';
import { useCertificates } from '../../services/hooks/useCertificates';
import { useAuthorities } from '../../services/hooks/useAuthorities';
import { AttachmentType } from '../../constants/enums';
import { formatBackendValidationErrors } from '../../helpers/validationHelpers';
import AddedCertificate, {
  AttachmentBaseInput,
  CertificateInput,
} from '../Molecules/AddedCertificate';

type CertificatesInput = {
  certificates: CertificateInput[];
};

const NationalCertificatePage: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const formikRef = useRef<any>();
  const [selectedCategory, setSelectedCategory] = useState<string>();
  const { application: currentApplication, setApplication } = useCurrentApplication();

  const {
    result: allCertificates,
    error: allCertificatesError,
    loading: allCertificatesLoading,
  } = useCertificates();

  const {
    result: allAuthorities,
    error: allAuthoritiesError,
    loading: allAuthoritiesLoading,
  } = useAuthorities();

  const {
    errorObject: submitError,
    loading: submitLoading,
    callApi: submitStep,
  } = useApi({
    apiFunction: (payload) => endorsementApi.upsertApplicationGroup(payload),
    callApiOnInit: false,
    successCallback: (result) => {
      if (result && setApplication) {
        setApplication({ result });
      }
      const path = generatePath(routes.requirements, { applicationUid: result.uid });
      navigate(path);
    },
  });

  useEffect(() => {
    if (submitError && submitError.response?.status === 422) {
      const errors = submitError?.response?.data?.validationErrors || [];
      const fieldErrors = formatBackendValidationErrors(errors);
      const currentValues = formikRef?.current?.values;
      formikRef?.current?.resetForm?.({ values: currentValues, errors: fieldErrors });
    }
  }, [submitError]);

  const certificateOptions = useMemo(() => {
    if (!allCertificates) return [];
    // eslint-disable-next-line max-len
    const certs: EndorsementServiceFeaturesGetAllCertificatesGetAllCertificatesDto[] =
      sortListByAttribute(allCertificates, `categoryName`);
    const items: SelectOption[] = [];
    certs?.forEach((group) => {
      group?.certificates?.forEach((c) => {
        items.push({
          name: c?.certificateName || '',
          value: c.uid || '',
          group: group?.categoryName || '',
          id: group?.categoryUid || '',
        });
      });
    });
    return items;
  }, [allCertificates, intl?.locale]);

  const filteredCertificateOptions = useMemo(() => {
    if (!certificateOptions) return [];
    const radioCategory = '5f9aa57b-9da9-4f4d-a306-1e206fd19055';
    const items: SelectOption[] = certificateOptions?.filter((item) => {
      if (!selectedCategory) return true;
      if (selectedCategory === radioCategory) {
        return item.id === radioCategory;
      }
      return item.id !== radioCategory;
    });
    return items;
  }, [certificateOptions, selectedCategory]);

  const allAuthoritiesOptions = useMemo(() => {
    if (!allAuthorities) return [];
    const authorities = allAuthorities.map((a) => ({
      name: a?.[intl?.locale] || '',
      value: a.uid || '',
    }));
    return sortListByAttribute(authorities, 'name');
  }, [allAuthorities, intl?.locale]);

  const requiredText = intl.formatMessage({ id: 'general.form.error' });

  const onNavBackClick = useCallback(() => {
    const path = generatePath(routes.sailor, {
      applicationUid: currentApplication?.result?.uid,
    });
    navigate(path);
  }, [currentApplication?.result?.uid]);

  const determinePayloadCertificates = useCallback(async (values: CertificatesInput) => {
    const certificates: EndorsementServiceFeaturesUpsertApplicationGroupCertificate[] = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const cert of values?.certificates || []) {
      if (cert?.certificateFiles) {
        const certAttachments: any[] = [];
        for (let i = 0; i < cert.certificateFiles.length; i += 1) {
          const f = cert.certificateFiles[i];
          if (f?.file) {
            // eslint-disable-next-line no-await-in-loop
            const cert64 = await toBase64(f.file);
            certAttachments.push({
              attachmentFileName: f?.name,
              attachmentType: AttachmentType.Certificate,
              attachmentFile: cert64 as string,
            });
          } else {
            certAttachments.push({
              attachmentFileName: f?.name,
              attachmentType: AttachmentType.Certificate,
              attachmentUid: f.uid,
            });
          }
        }
        const expiry = cert?.expirationDate
          ? moment(cert.expirationDate).format(moment.HTML5_FMT.DATE)
          : cert?.expirationDate || undefined;
        certificates.push({
          applicationUid: cert?.applicationUid || undefined,
          certificateAuthorityUid: cert.issuedBy,
          certificateNumber: cert.certificateNumber,
          expirationDate: expiry,
          uid: cert.requestedCertificate,
          existingCertificateUid: cert.certificate,
          attachments: certAttachments,
        });
      }
    }
    return certificates;
  }, []);

  const onCategorySelected = (categoryUid?: string) => {
    if (!categoryUid) return;
    setSelectedCategory(categoryUid);
  };

  const handleSubmit = useCallback(
    async (values: CertificatesInput) => {
      if (!currentApplication?.result) return;

      /* Validate attachments */
      const errors: FormikErrors<any> = {};
      values?.certificates?.forEach((c, i) => {
        if (!c.certificateFiles || c?.certificateFiles?.length === 0) {
          errors[`certificateFiles.${i}`] = requiredText;
        } else if (c.certificateFiles.some((f) => f.file?.size === 0)) {
          errors[`certificateFiles.${i}`] = intl.formatMessage({
            id: 'general.form.error.emptyfile',
          });
        }
      });
      if (Object.keys(errors).length) {
        formikRef?.current?.setErrors(errors);
        return;
      }
      /* End of validate attachments */

      const certificates = await determinePayloadCertificates(values);
      const payload: UpsertApplicationGroupRequest = {
        ...currentApplication?.result,
        certificates,
      };
      submitStep(payload);
    },
    [currentApplication?.result]
  );

  const validationSchema: Yup.SchemaOf<any> = Yup.object().shape({
    certificates: Yup.array().of(
      Yup.object().shape({
        certificate: Yup.string().required(requiredText).nullable(),
        certificateNumber: Yup.string().required(requiredText),
        issuedBy: Yup.string().required(requiredText).nullable(),
        expirationDate: Yup.mixed()
          .required(requiredText)
          .test(
            'not-past',
            intl.formatMessage({ id: 'general.form.error.datemustbefuture' }),
            (v) => {
              return moment(v).isAfter(moment());
            }
          ),
        requestedCertificate: Yup.string().required(requiredText).nullable(),
      })
    ),
  });

  const initialValues: CertificatesInput = useMemo(() => {
    const application = currentApplication?.result;
    const initialCertificates: CertificateInput[] = [];
    const previouslyAdded = application?.certificates || [];
    if (previouslyAdded.length > 0) {
      previouslyAdded.forEach((cert) => {
        const prev = cert?.attachments || null;
        const attachments: AttachmentBaseInput[] =
          prev?.map((a) => ({ uid: a.attachmentUid || '', name: a.attachmentFileName || '' })) ||
          [];
        initialCertificates.push({
          applicationUid: cert?.applicationUid || null,
          certificate: cert?.existingCertificateUid || null,
          issuedBy: cert?.certificateAuthorityUid || null,
          certificateFiles: attachments || null,
          previousAttachments: cert?.attachments || null,
          requestedCertificate: cert?.uid || null,
          certificateNumber: cert?.certificateNumber || '',
          expirationDate: cert?.expirationDate || '',
        });
      });
    } else {
      initialCertificates.push({
        applicationUid: null,
        certificate: null,
        issuedBy: null,
        certificateFiles: null,
        previousAttachments: null,
        requestedCertificate: null,
        certificateNumber: '',
        expirationDate: '',
      });
    }
    const initialUid = initialCertificates?.[0]?.certificate;
    if (initialUid) {
      const group = certificateOptions?.find((o) => o.value === initialUid);
      onCategorySelected?.(group?.id || undefined);
    }
    return {
      certificates: initialCertificates,
    };
  }, [certificateOptions, currentApplication?.result]);

  if (allAuthoritiesLoading || allCertificatesLoading || currentApplication?.loading) {
    return <Loader />;
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      innerRef={formikRef}
      enableReinitialize
    >
      {({ submitForm, setFieldValue, values, errors }) => (
        <form>
          <ApplicationLayout
            canNavNext={!submitLoading}
            canNavBack={!submitLoading}
            heading={intl.formatMessage({ id: 'application.new.title' })}
            ingress={intl.formatMessage({ id: 'application.new.ingress' })}
            exitLabel={intl.formatMessage({ id: 'general.backtoapplications' })}
            navBackLabel={intl.formatMessage({ id: 'general.back' })}
            navNextLabel={intl.formatMessage({ id: 'general.next' })}
            timeline={<ApplicationSteps />}
            onNavBackClick={onNavBackClick}
            onNavNextClick={submitForm}
          >
            <DataCard>
              <CertInfo>
                <h2>{intl.formatMessage({ id: 'application.certificate.title' })}</h2>
                <FieldArray
                  name="certificates"
                  render={(arrayHelpers) => (
                    <>
                      {(values?.certificates || [])?.map((cert, index) => (
                        <React.Fragment key={index}>
                          {index > 0 && <hr />}
                          <AddedCertificate
                            allCertificates={allCertificates || []}
                            application={currentApplication?.result}
                            authoritiesOptions={allAuthoritiesOptions}
                            certificate={cert}
                            certificateOptions={certificateOptions}
                            filteredCertificateOptions={filteredCertificateOptions}
                            index={index}
                            selectedCategory={selectedCategory}
                            onCategorySelected={onCategorySelected}
                            setFieldValue={setFieldValue}
                            errors={errors}
                          />
                          <AddRemoveButtons>
                            {index > 0 && (
                              <SdirButton type="danger" onClick={() => arrayHelpers.remove(index)}>
                                {intl.formatMessage({ id: 'application.certificate.remove' })}
                              </SdirButton>
                            )}
                            {index + 1 === values?.certificates?.length && (
                              <SdirButton
                                type="default"
                                disabled={!selectedCategory}
                                onClick={() => arrayHelpers.insert(index + 1, {})}
                              >
                                {intl.formatMessage({ id: 'application.certificate.add' })}
                              </SdirButton>
                            )}
                          </AddRemoveButtons>
                        </React.Fragment>
                      ))}
                    </>
                  )}
                />
                {(values?.certificates || []).length > 1 && (
                  <InfoWrapper>
                    <AlertShort
                      text={intl.formatMessage({ id: 'application.certificate.radioinfo' })}
                      type="info"
                    />
                  </InfoWrapper>
                )}
              </CertInfo>
            </DataCard>
            {submitError && (
              <ErrorWrapper>
                <ErrorMessage error={submitError} />
              </ErrorWrapper>
            )}
          </ApplicationLayout>
        </form>
      )}
    </Formik>
  );
};

export default NationalCertificatePage;

const CertInfo = styled.div`
  padding: 10px 35px 40px 35px;
  min-height: 400px;

  hr {
    margin: 10px 0 30px 0;
    border-top: 1px solid ${({ theme }) => theme.colors.primary8};
  }
`;

const InfoWrapper = styled.div`
  margin-top: 20px;
`;

const ErrorWrapper = styled.div`
  margin-bottom: 15px;
`;

const AddRemoveButtons = styled.div`
  display: flex;
  flex-direction: row;

  button:first-child {
    margin-right: 10px;
  }
`;
